import type { TaskListMode } from "../../domain/app-types.ts";
import type { Todo } from "../../domain/entities.ts";
import { escapeHtml } from "../../ui/text.ts";

export interface TaskCardIcons {
  grip: string;
  up: string;
  down: string;
  check: string;
  open: string;
  close?: string;
  pin?: string;
}

interface SharedTaskCardView {
  todo: Todo;
  selecting: boolean;
  editable: boolean;
  blocked: boolean;
  titleHtml: string;
  metadataHtml: string;
  bulkSelectorHtml: string;
  icons: TaskCardIcons;
}

export interface BoardCardViewModel extends SharedTaskCardView {
  mode: "simple" | "detailed";
  descriptionHtml: string;
  moveColumns: string[];
  currentColumn: string | null;
  overdue: boolean;
}

export interface TaskRowViewModel extends SharedTaskCardView {
  mode: TaskListMode;
  reorderEnabled: boolean;
  myDay: boolean;
  descriptionHtml: string;
  syncStatusHtml: string;
}

function stateClasses(todo: Todo, selecting: boolean): string {
  return `${selecting ? "is-selecting" : ""} ${todo.pinned ? "is-pinned" : ""} ${todo.priority === "urgent" ? "is-urgent" : ""} ${todo.completed ? "is-complete" : ""}`;
}

function completionButton(model: SharedTaskCardView): string {
  const { todo, editable, blocked, icons } = model;
  return `<button class="check-button" data-action="toggle" data-id="${todo.id}" aria-label="${todo.completed ? "Mark incomplete" : blocked ? "Task is blocked" : "Complete task"}" aria-pressed="${todo.completed}" ${editable && (!blocked || todo.completed) ? "" : "disabled"}>${icons.check}</button>`;
}

function reorderControls(todo: Todo, icons: TaskCardIcons, board: boolean, enabled = true): string {
  const attribute = board ? "data-move-board-task" : "data-move-task";
  const suffix = board ? " in this column" : "";
  return `<div class="reorder-controls ${enabled ? "" : "is-disabled"}">${board ? "" : `<span class="drag-handle" aria-hidden="true">${icons.grip}</span>`}<button ${attribute}="up" aria-label="Move ${escapeHtml(todo.title || "task")} up${suffix}">${icons.up}</button><button ${attribute}="down" aria-label="Move ${escapeHtml(todo.title || "task")} down${suffix}">${icons.down}</button></div>`;
}

export function boardCardView(model: BoardCardViewModel): string {
  const { todo, selecting, editable, mode, titleHtml, descriptionHtml, metadataHtml, bulkSelectorHtml, icons } = model;
  const destinations = [...model.moveColumns.map(column => `<option value="${escapeHtml(column)}" ${model.currentColumn === column ? "disabled" : ""}>${escapeHtml(column)}</option>`), model.currentColumn !== null ? `<option value="">Unassigned</option>` : ""].join("");
  return `<article class="kanban-card kanban-card--${mode} ${stateClasses(todo, selecting)} ${model.blocked ? "is-blocked" : ""} ${model.overdue ? "is-overdue" : ""}" data-todo="${todo.id}" tabindex="-1" draggable="${!selecting && editable}">
    <div class="kanban-card__top">${selecting ? bulkSelectorHtml : completionButton(model)}${selecting ? `<strong class="kanban-card__title">${titleHtml}</strong>` : `<button type="button" class="kanban-card__title" data-action="edit" data-id="${todo.id}" aria-label="View ${escapeHtml(todo.title)}"><strong>${titleHtml}</strong></button>`}${!selecting && editable ? `<details class="kanban-card__actions"><summary aria-label="Actions for ${escapeHtml(todo.title || "task")}" title="Task actions">${icons.grip}</summary><div class="kanban-card__action-menu">${reorderControls(todo, icons, true)}<label class="kanban-card__move-label">Move to column<select data-board-move-to="${todo.id}" aria-label="Move ${escapeHtml(todo.title || "task")} to column"><option value="__choose" selected disabled>Choose column…</option>${destinations}</select></label></div></details>` : ""}</div>
    ${mode === "detailed" && todo.description ? `<p>${descriptionHtml}</p>` : ""}
    ${metadataHtml ? `<div class="todo-meta">${metadataHtml}</div>` : ""}
  </article>`;
}

export function taskRowView(model: TaskRowViewModel): string {
  const { todo, selecting, editable, mode, reorderEnabled, myDay, titleHtml, descriptionHtml, metadataHtml, syncStatusHtml, bulkSelectorHtml, icons } = model;
  const reorder = selecting ? bulkSelectorHtml : reorderControls(todo, icons, false, reorderEnabled);
  const leave = myDay ? `<button class="icon-button my-day-leave" data-action="leave-my-day" data-id="${todo.id}" aria-label="Remove ${escapeHtml(todo.title)} from My Day" title="Remove from My Day · task stays in its collection">${icons.close || ""}</button>` : "";
  if (mode === "simple") return `<article class="todo-row todo-row--simple ${stateClasses(todo, selecting)}" data-todo="${todo.id}" tabindex="-1" draggable="${!selecting && editable && reorderEnabled}">
    ${reorder}
    ${selecting ? "" : completionButton(model)}
    <strong><span class="todo-title">${titleHtml}</span></strong>${leave}
  </article>`;
  return `<article class="todo-row ${stateClasses(todo, selecting)}" data-todo="${todo.id}" tabindex="-1" draggable="${!selecting && editable && reorderEnabled}">
    ${reorder}
    ${selecting ? "" : completionButton(model)}
    <div class="todo-copy"><strong>${syncStatusHtml}${todo.pinned ? `<span class="task-pin" role="img" title="Pinned" aria-label="Pinned">${icons.pin || ""}</span>` : ""}<span class="todo-title">${titleHtml}</span></strong>${todo.description ? `<p>${descriptionHtml}</p>` : ""}<div class="todo-meta" role="group" aria-label="Task details">${metadataHtml}</div></div>
    ${leave}<button class="icon-button row-menu" data-action="edit" data-id="${todo.id}" aria-label="View ${escapeHtml(todo.title)}">${icons.open}</button>
  </article>`;
}
